import { Head, Link, router } from "@inertiajs/react"
import { Archive, ArrowLeft, RotateCcw } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import type { ArchivedAutomation } from "@/types/automation"

const DESCRIPTION =
  "Automations retired after they had already run: stopped, out of every organization's catalog, their history kept. Reactivate one to offer it again."

function plural(count: number, noun: string) {
  return `${count} ${count === 1 ? noun : `${noun}s`}`
}

export default function AdminAutomationsArchived({
  automations,
}: {
  automations: ArchivedAutomation[]
}) {
  return (
    <>
      <Head title="Archived automations">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Archived automations" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <Link
          href="/admin/automations"
          className="mb-4 inline-flex items-center gap-1.5 text-sm no-underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to automations
        </Link>

        <PageHeader
          title="Archived automations"
          description="An automation that has run is archived rather than deleted, so the record of who was texted what survives. Nothing here runs or appears in any organization's catalog."
        />

        <div className="mt-6">
          <FlashNotice />

          {automations.length === 0 ? (
            <div className="callout">
              <p className="text-sm text-ink-muted">
                Nothing is archived. Deleting an automation that has already run
                archives it here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {automations.map((automation) => (
                <li
                  key={automation.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <Archive className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink-display">
                          {automation.name}
                        </span>
                        {!automation.is_active && <Badge tone="muted">Inactive</Badge>}
                      </div>
                      <div className="truncate text-xs text-ink-muted">
                        {automation.description || "No description"}
                      </div>
                      <div className="mt-0.5 text-xs text-ink-muted">
                        Archived {automation.archived_at_label}
                        {automation.archived_by ? ` by ${automation.archived_by}` : ""} ·{" "}
                        {plural(automation.run_count, "run")} ·{" "}
                        {plural(automation.setup_count, "setup")}, all switched off
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pl-7 sm:pl-0">
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        href={`/runs?automation_id=${automation.id}`}
                        className="no-underline"
                      >
                        View history
                      </Link>
                    </Button>
                    <Reactivate automation={automation} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </AdminShell>
    </>
  )
}

function Reactivate({ automation }: { automation: ArchivedAutomation }) {
  const reactivate = () => {
    router.post(`/admin/automations/${automation.id}/reactivate`)
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          <RotateCcw className="h-3.5 w-3.5" />
          Reactivate
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reactivate {automation.name}?</DialogTitle>
          <DialogDescription>
            {automation.is_active
              ? "It goes back into every organization's catalog."
              : "It comes back inactive, as it was when archived, so it stays out of the catalog until an admin makes it active."}{" "}
            Every setup stays switched off, so each organization decides again
            whether to send.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Leave archived</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button onClick={reactivate}>Reactivate</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
